// src/components/OrderConfirmationPage.jsx
//
// Handles two navigation sources:
//
//  A) REST checkout flow (from CheckoutPage.js):
//     state = { orderResponse, shippingInfo, billingAddress, paymentMethod,
//               cardLastFour, subtotal, shipping, tax, discount, total, orderedItems }
//
//  B) Agent / chat flow (from ChatBot.js confirm_purchase):
//     state = { isAgentFlow: true, message, totalAmount, purchasedItems,
//               articleIds, orderedItems, conversationId }
//
import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { trackCheckoutStep } from "../services/uiAgentSync";
import {
  normalizeAgentLines,
  normalizeAgentTotals,
  buildBillRowsFromTotals,
  DEFAULT_AGENT_TAX_PERCENT,
  PRICE_MATCH_ADJUSTMENT_LABEL,
} from "../utils/agentBillingPayload";
// ── Helpers ───────────────────────────────────────────────────────────────

function fmt(price) {
  const n = typeof price === "string" ? parseFloat(price) : price;
  return isNaN(n) || n == null ? "0.00" : n.toFixed(2);
}

function roundMoney(n) {
  return Math.round(parseFloat(String(n ?? 0)) * 100) / 100;
}

function fmtAddress(addr = {}) {
  return [
    `${addr.firstName || addr.first_name || ""} ${addr.lastName || addr.last_name || ""}`.trim(),
    addr.streetAddress || addr.street_address,
    addr.city,
    addr.state,
    addr.zipCode || addr.zip_code,
    addr.country,
  ].filter(Boolean).join(", ") || "—";
}

function makeOrderNumber(orderResponse = {}) {
  return (
    orderResponse.order_id ||
    orderResponse.order_number ||
    orderResponse.id ||
    `ORD-${Math.random().toString(36).substring(2, 10).toUpperCase()}`
  );
}

// ── Main component ────────────────────────────────────────────────────────

export default function OrderConfirmationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearCart } = useCart();

  const state = location.state;

  // Guard: no state at all → someone visited the URL directly
  useEffect(() => {
    if (!state) navigate("/", { replace: true });
  }, [state, navigate]);

  // Clear cart once after a successful order confirmation load (Cart API).
  useEffect(() => {
    if (!state) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await clearCart({ silent: true });
      } catch (err) {
        if (!cancelled) {
          console.warn("[OrderConfirmation] clearCart failed:", err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state, clearCart]);

  // UI → Agent sync: order successfully placed (deduped in the tracker)
  useEffect(() => {
    if (state) {
      trackCheckoutStep("order_placed");
    }
  }, [state]);

  if (!state) return null;

  // Agent / chat purchase — detect via flag or payload shape from OrbieeFlow
  const isAgentFlow = Boolean(
    state.isAgentFlow ||
      state.fromChat ||
      (Array.isArray(state.purchasedItems) && state.purchasedItems.length > 0) ||
      state.totalAmount != null
  );

  // ── Data extraction — works for both state shapes ─────────────────────

  const orderDate = new Date();
  const deliveryDate = new Date(orderDate);
  deliveryDate.setDate(deliveryDate.getDate() + 5);

  let orderNumber, shippingDisplay, paymentDisplay, subtotal, shipping, tax, discount, total, items;

  if (isAgentFlow && !state.orderResponse) {
    // Agent / chat-driven purchase — bill from agent lines + totals
    const agentLines = normalizeAgentLines(
      Array.isArray(state.lines) && state.lines.length
        ? state.lines
        : state.summary?.lines
    );
    const agentTotals = normalizeAgentTotals(
      state.totals ?? state.summary?.totals
    );

    const orderIds = state.summary?.order_ids;
    orderNumber =
      Array.isArray(orderIds) && orderIds.length
        ? String(orderIds[0])
        : makeOrderNumber({});

    shippingDisplay = "Confirmed via Orbiee Assistant";
    paymentDisplay = "Processed by Orbiee Assistant";

    if (agentTotals) {
      console.log("agentTotals", agentTotals);
      subtotal = agentTotals.subtotal;
      shipping = agentTotals.shipping;
      tax = agentTotals.tax;
      discount = agentTotals.discountTotal;
      total = agentTotals.total;
    } else {
      const agentTotal =
        Number(state.totalAmount ?? state.summary?.total ?? 0) || 0;
      const lineSubtotal = agentLines.reduce(
        (sum, line) => sum + (line.lineTotal ?? (line.price ?? 0) * (line.quantity ?? 1)),
        0
      );
      subtotal = lineSubtotal > 0 ? lineSubtotal : agentTotal;
      shipping = 0;
      discount = 0;
      total = agentTotal || subtotal;
      const derived = normalizeAgentTotals({ subtotal, total });
      tax = derived?.tax ?? roundMoney(subtotal * DEFAULT_AGENT_TAX_PERCENT / 100);
      if (derived) {
        total = derived.total;
      } else if (total <= subtotal) {
        total = roundMoney(subtotal + tax);
      }
    }

    if (agentLines.length > 0) {
      items = agentLines;
    } else {
      const rawItems =
        (Array.isArray(state.purchasedItems) && state.purchasedItems.length
          ? state.purchasedItems
          : null) ||
        (Array.isArray(state.orderedItems) && state.orderedItems.length
          ? state.orderedItems
          : null) ||
        (Array.isArray(state.summary?.items) ? state.summary.items : []) ||
        [];
      items = rawItems.map((item) => ({
        ...item,
        id: item.id ?? item.product_id ?? item.article_id,
        title: item.title || item.name || item.product_title || "Item",
        quantity: item.quantity ?? 1,
        price: parseFloat(String(item.price ?? 0)) || 0,
        image: item.image || item.image_uri || item.uri || "",
        image_uri: item.image_uri || item.image || item.uri || "",
        size: item.size ?? null,
      }));
    }
  } else {
    // Standard REST checkout (manual) — leave details as provided by CheckoutPage
    orderNumber = makeOrderNumber(state.orderResponse || {});
    shippingDisplay = fmtAddress(state.shippingInfo || {});
    const paymentMethod = state.paymentMethod || "card";
    paymentDisplay =
      paymentMethod === "paypal"
        ? "PayPal"
        : paymentMethod === "applepay"
        ? "Apple Pay"
        : `Credit Card${state.cardLastFour ? ` ending in ${state.cardLastFour}` : ""}`;
    subtotal = state.subtotal ?? 0;
    shipping = 0;
    discount = state.discount ?? 0;
    const taxableSubtotal = Math.max(0, subtotal - discount);
    tax =
      state.tax ??
      roundMoney(taxableSubtotal * DEFAULT_AGENT_TAX_PERCENT / 100);
    total =
      state.total ?? roundMoney(taxableSubtotal + tax);
    items = state.orderedItems || [];
  }

  const customerEmail = state.shippingInfo?.email || null;

  const agentTotalsForRows = isAgentFlow && !state.orderResponse
    ? normalizeAgentTotals(
        state.totals ??
          state.summary?.totals ?? {
            subtotal,
            total,
          }
      )
    : null;

  const priceRows = agentTotalsForRows
  ? buildBillRowsFromTotals(
    { ...agentTotalsForRows, subtotal: agentTotalsForRows.originalSubtotal },
    fmt
  )
    : [
        { label: "Subtotal", value: `$${fmt(subtotal)}` },
        {
          label: `Tax (${DEFAULT_AGENT_TAX_PERCENT}%)`,
          value: `$${fmt(tax)}`,
        },
        ...(discount > 0
          ? [{ label: PRICE_MATCH_ADJUSTMENT_LABEL, value: `-$${fmt(discount)}`, green: true }]
          : []),
        { label: "Total", value: `$${fmt(total)}`, bold: true },
      ];


  // ── Render ─────────────────────────────────────────────────────────────

  const dateLong = (d) =>
    d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const timeline = [
    {
      label: "Order confirmed",
      caption: dateLong(orderDate),
      body: "Your order has been received and confirmed.",
      state: "done",
    },
    {
      label: "Processing",
      caption: "In progress",
      body: "We are preparing your items for shipment.",
      state: "active",
    },
    {
      label: "Shipped",
      caption: "Pending",
      body: "You will receive tracking information soon.",
      state: "todo",
    },
    {
      label: "Delivered",
      caption: `Est. ${dateLong(deliveryDate)}`,
      body: "Estimated delivery date.",
      state: "todo",
    },
  ];

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-10 ob-viewin">
      {/* ── Hero ── */}
      <div className="text-center mb-8">
        <div className="ob-successmark" aria-hidden="true">
          <span className="ob-successmark-ring" />
          <span className="ob-successmark-ring ob-successmark-ring-2" />
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-8 h-8 relative"
          >
            <path d="m4 12.5 5 5L20 6.5" className="ob-tick" />
          </svg>
        </div>

        <span className="ob-eyebrow block mt-5">
          {isAgentFlow ? "Completed by Orbiee" : "Order placed"}
        </span>
        <h1 className="ob-display text-[clamp(2rem,5vw,2.9rem)] leading-[1.05] mt-1.5">
          Order confirmed
        </h1>
        <p className="text-[13.5px] ob-ink2 mt-3 max-w-[460px] mx-auto leading-relaxed">
          {isAgentFlow
            ? state.message ||
              "Your purchase has been completed by Orbiee Assistant."
            : "Thank you for your purchase. Your order has been received."}
        </p>
        {customerEmail && (
          <p className="text-[12px] ob-ink3 mt-2">
            A confirmation is on its way to{" "}
            <strong style={{ color: "var(--ob-ink-2)" }}>{customerEmail}</strong>
            .
          </p>
        )}
      </div>

      {/* ── Order banner ── */}
      <div className="ob-orderbanner ob-risein">
        <span
          className="ob-blob"
          style={{
            width: 220,
            height: 220,
            top: -110,
            right: -40,
            background: "rgba(255,255,255,.7)",
          }}
        />
        <span
          className="ob-blob"
          style={{
            width: 170,
            height: 170,
            bottom: -110,
            left: 60,
            background: "rgba(111,212,255,.35)",
            animationDelay: "-7s",
          }}
        />

        <div className="relative flex flex-wrap items-center gap-4 justify-between">
          <div>
            <div
              className="text-[9.5px] font-bold uppercase tracking-[.16em]"
              style={{ color: "var(--ob-ink-2)" }}
            >
              Order number
            </div>
            <div className="text-[19px] font-bold mt-1 font-mono tracking-tight" style={{ color: "var(--ob-ink)" }}>
              {orderNumber}
            </div>
            <div
              className="text-[11.5px] mt-1"
              style={{ color: "var(--ob-ink-2)" }}
            >
              Placed on {dateLong(orderDate)}
            </div>
          </div>

          <div className="flex gap-2">
            <button type="button" className="ob-btn ob-btn-sm ob-btn-onhero">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-[14px] h-[14px]"
              >
                <path d="M12 4v11m0 0 4-4m-4 4-4-4M5 20h14" />
              </svg>
              Invoice
            </button>
            {customerEmail && (
              <button
                type="button"
                className="ob-btn ob-btn-sm ob-btn-onhero-solid"
                onClick={() => (window.location.href = `mailto:${customerEmail}`)}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-[14px] h-[14px]"
                >
                  <rect x="3" y="5" width="18" height="14" rx="2.5" />
                  <path d="m3.5 7 8.5 6 8.5-6" />
                </svg>
                Email
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr,330px] gap-5 items-start mt-5">
        <div className="space-y-5">
          {/* ── Items ── */}
          <section className="ob-panel p-5">
            <h2 className="text-[13px] font-semibold ob-ink mb-3">
              What you ordered
              {items.length > 0 ? ` · ${items.length}` : ""}
            </h2>

            {items.length > 0 ? (
              <div className="flex flex-col">
                {items.map((item, index) => {
                  const img = Array.isArray(item.image)
                    ? item.image[0]
                    : item.image || item.image_uri || "";
                  const title =
                    item.title ||
                    item.name ||
                    item.product_title ||
                    item.article_id ||
                    "Item";
                  const qty = item.quantity ?? 1;
                  const isAgentLine = item.priceMatched != null;
                  const lineTotal = isAgentLine
                    ? item.lineTotal ??
                      (parseFloat(String(item.price ?? 0)) || 0) * qty
                    : (parseFloat(String(item.price ?? 0)) || 0) * qty;

                  return (
                    <div
                      key={
                        item.cartLineUid ||
                        item.id ||
                        item.product_id ||
                        index
                      }
                      className="flex items-center gap-3 py-3 ob-hair-b last:border-0"
                    >
                      <div className="ob-orderthumb">
                        {img ? (
                          <img
                            src={img}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : null}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-semibold ob-ink leading-snug">
                          {title}
                        </div>
                        <div className="text-[11px] ob-ink3 mt-0.5">
                          {item.size ? `Size ${item.size} · ` : ""}Qty {qty}
                        </div>
                      </div>

                      {isAgentLine &&
                      item.priceMatched &&
                      item.originalLineTotal != null ? (
                        <div className="text-right shrink-0">
                          <div className="text-[10.5px] ob-ink3 line-through">
                            ${fmt(item.originalLineTotal)}
                          </div>
                          <div
                            className="text-[13.5px] font-bold ob-tabnum"
                            style={{ color: "var(--ob-ok)" }}
                          >
                            ${fmt(lineTotal)}
                          </div>
                        </div>
                      ) : lineTotal > 0 ? (
                        <div className="text-[13.5px] font-bold ob-tabnum shrink-0 ob-ink">
                          ${fmt(lineTotal)}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-[12.5px] ob-ink3 py-2">
                Item details are not available for this order.
              </p>
            )}

            {/* Price breakdown */}
            {total > 0 && (
              <div className="mt-4 pt-4 ob-hair-t">
                {priceRows.map(({ label, value, bold, green, free }) => (
                  <div
                    key={label}
                    className={`flex justify-between py-1 ${
                      bold
                        ? "text-[15.5px] font-bold pt-3 mt-1.5 ob-hair-t ob-ink"
                        : "text-[12.5px] ob-ink2"
                    }`}
                  >
                    <span>{label}</span>
                    <span
                      className="ob-tabnum"
                      style={
                        green || free
                          ? { color: "var(--ob-ok)", fontWeight: 600 }
                          : bold
                          ? undefined
                          : { color: "var(--ob-ink)", fontWeight: 600 }
                      }
                    >
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ── Timeline ── */}
          <section className="ob-panel p-5">
            <h2 className="text-[13px] font-semibold ob-ink mb-4">
              Order timeline
            </h2>
            <ol className="ob-timeline">
              {timeline.map(({ label, caption, body, state: st }) => (
                <li key={label} className="ob-tl-item" data-state={st}>
                  <span className="ob-tl-dot" aria-hidden="true">
                    {st === "done" && (
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={3}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-2.5 h-2.5"
                      >
                        <path d="m4 12.5 5 5L20 6.5" />
                      </svg>
                    )}
                  </span>
                  <div className="pb-5">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-[13px] font-semibold ob-ink">
                        {label}
                      </span>
                      <span className="text-[10.5px] ob-ink3">{caption}</span>
                    </div>
                    <p className="text-[12px] ob-ink2 mt-0.5 leading-relaxed">
                      {body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>

        {/* ── Side column ── */}
        <aside className="space-y-5">
          <section className="ob-panel p-5">
            <div className="flex items-center gap-2 mb-2.5">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-4 h-4"
                style={{ color: "var(--ob-agent)" }}
              >
                <path d="M12 21s7-5.7 7-11a7 7 0 1 0-14 0c0 5.3 7 11 7 11Z" />
                <circle cx="12" cy="10" r="2.6" />
              </svg>
              <span className="text-[13px] font-semibold ob-ink">
                Shipping
              </span>
            </div>
            <p className="text-[12.5px] ob-ink2 leading-relaxed">
              {shippingDisplay}
            </p>
            {!isAgentFlow && state.shippingInfo?.phone && (
              <p className="text-[12px] ob-ink3 mt-1">
                {state.shippingInfo.phone}
              </p>
            )}
          </section>

          <section className="ob-panel p-5">
            <div className="flex items-center gap-2 mb-2.5">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-4 h-4"
                style={{ color: "var(--ob-ok)" }}
              >
                <rect x="3" y="5" width="18" height="14" rx="2.5" />
                <path d="M3 10h18" />
              </svg>
              <span className="text-[13px] font-semibold ob-ink">Payment</span>
            </div>
            <p className="text-[12.5px] font-medium ob-ink">{paymentDisplay}</p>
            <div className="ob-chip ob-chip-ok mt-2.5">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.4}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-3 h-3"
              >
                <path d="m4 12.5 5 5L20 6.5" />
              </svg>
              Payment successful
            </div>
          </section>

          <section className="ob-panel p-5 text-center">
            <div className="text-[13px] font-semibold ob-ink">Need a hand?</div>
            <p className="text-[12px] ob-ink2 mt-1 leading-relaxed">
              Our team is here, or just ask Orbiee.
            </p>
            <div className="flex flex-col gap-2 mt-4">
              <button
                type="button"
                className="ob-btn ob-btn-solid ob-btn-block"
                onClick={() => navigate("/")}
              >
                Keep shopping
              </button>
              <button
                type="button"
                className="ob-btn ob-btn-ghost ob-btn-block"
                onClick={() => navigate("/order-history")}
              >
                View order history
              </button>
              <button
                type="button"
                className="ob-btn ob-btn-quiet ob-btn-sm ob-btn-block ob-ink3"
                onClick={() =>
                  (window.location.href = "mailto:support@orbiee.com")
                }
              >
                Contact support
              </button>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
